import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getProjects,
  createProject,
  getProject,
  updateProject,
  linkSpace,
  unlinkSpace,
  addProjectMember,
  removeProjectMember,
  getProjectReportSummary,
  getProjectTasks,
  getProjectMileage,
  getProjectTime,
} from '../services/projectService';
import { listUsers } from '../services/userService';
import { getBusinesses } from '../services/businessService';
import { useSpaces } from '../context/SpaceContext';
import { getProjectBudget, upsertProjectBudget } from '../services/budgetService';
import { getProjectFundingAllocations } from '../services/fundingService';
import { getProjectExpenses, createProjectExpense, transitionExpenseState } from '../services/expenseService';
import { getVisibleProjectTabs, hasAnyProjectCapability } from '../utils/projectWorkspaceNav';
import './ProjectDetailPage.css';

const ROLE_OPTIONS = ['owner', 'admin', 'member', 'viewer', 'accounting'];
const LIFECYCLE_STATES = [
  'idea',
  'funding-review',
  'funding-pending',
  'approved',
  'active',
  'delivery-complete',
  'reporting',
  'closed',
  'on-hold',
  'cancelled',
];
const LIFECYCLE_LABELS = {
  idea: 'Idea',
  'funding-review': 'Funding Review',
  'funding-pending': 'Funding Pending',
  approved: 'Approved',
  active: 'Active',
  'delivery-complete': 'Delivery Complete',
  reporting: 'Reporting',
  closed: 'Closed',
  'on-hold': 'On Hold',
  cancelled: 'Cancelled',
};

const formatLifecycle = (state) => LIFECYCLE_LABELS[state] || state || 'Active';
const tabLabel = (value) => value.charAt(0).toUpperCase() + value.slice(1);

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { spaces = [], createSpace, fetchSpaces } = useSpaces() || {};

  const [projects, setProjects] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [tab, setTab] = useState('overview');
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectBusinessId, setNewProjectBusinessId] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  // Budget & Financial State
  const [budget, setBudget] = useState(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetLines, setBudgetLines] = useState([]);
  const [newLineCategory, setNewLineCategory] = useState('');
  const [newLinePlanned, setNewLinePlanned] = useState('');
  const [savingBudget, setSavingBudget] = useState(false);

  // Funding Allocations State
  const [allocations, setAllocations] = useState([]);
  const [allocationsLoading, setAllocationsLoading] = useState(false);

  // Expenses State
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [showNewExpense, setShowNewExpense] = useState(false);
  const [newExpenseDesc, setNewExpenseDesc] = useState('');
  const [newExpenseAmount, setNewExpenseAmount] = useState('');
  const [newExpenseCategory, setNewExpenseCategory] = useState('');
  const [newExpenseSourceId, setNewExpenseSourceId] = useState('');
  const [savingExpense, setSavingExpense] = useState(false);

  // Operational State
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [mileageLogs, setMileageLogs] = useState([]);
  const [mileageLoading, setMileageLoading] = useState(false);
  const [totalKm, setTotalKm] = useState(0);
  const [timeLogs, setTimeLogs] = useState([]);
  const [timeLoading, setTimeLoading] = useState(false);
  const [totalHours, setTotalHours] = useState(0);

  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const [spaceIdInput, setSpaceIdInput] = useState('');
  const [linkingSpace, setLinkingSpace] = useState(false);
  const [showNewWorkroom, setShowNewWorkroom] = useState(false);
  const [newWorkroomName, setNewWorkroomName] = useState('');
  const [creatingWorkroom, setCreatingWorkroom] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');

  const isDetailView = Boolean(projectId);

  useEffect(() => {
    listUsers()
      .then((res) => setAllUsers(res.data.users || []))
      .catch((err) => console.error('Failed to load users:', err));
  }, []);

  const loadSummary = async (id) => {
    setSummaryLoading(true);
    try {
      const res = await getProjectReportSummary(id);
      setSummary(res.data.summary || null);
      setSummaryError('');
    } catch (err) {
      setSummary(null);
      setSummaryError(err?.response?.data?.message || 'No reporting access for this project summary.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const refreshProject = useCallback(async (id) => {
    const res = await getProject(id);
    const p = res.data.project;
    setSelectedProject(p);
    setEditData({
      ...p,
      client: p.client?._id || p.client || '',
      leadBusinessId: p.leadBusinessId?._id || p.leadBusinessId || '',
      lifecycleState: p.lifecycleState || 'active',
    });
    return p;
  }, []);

  useEffect(() => {
    setListLoading(true);
    Promise.all([getProjects(), getBusinesses()])
      .then(([projRes, businessRes]) => {
        setProjects(projRes.data.projects || []);
        const manageableBusinesses = (businessRes.data.businesses || []).filter((business) =>
          (business.currentUserCapabilities || []).includes('project.manage')
        );
        setBusinesses(manageableBusinesses);
        if (manageableBusinesses.length === 1) setNewProjectBusinessId(manageableBusinesses[0]._id);
      })
      .catch((err) => console.error('Failed to load projects:', err))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => {
    if (!projectId) return;
    refreshProject(projectId)
      .then(() => loadSummary(projectId))
      .catch((err) => console.error('Failed to load project:', err));
  }, [projectId, refreshProject]);

  const currentCapabilities = useMemo(() => selectedProject?.currentUserCapabilities || [], [selectedProject]);
  const visibleTabs = useMemo(() => getVisibleProjectTabs(currentCapabilities), [currentCapabilities]);
  const canManageProject = currentCapabilities.includes('project.manage');
  const canViewBudget = hasAnyProjectCapability(currentCapabilities, ['budget.view', 'budget.manage']);
  const canManageBudget = currentCapabilities.includes('budget.manage');
  const canViewFunding = hasAnyProjectCapability(currentCapabilities, ['funding.view', 'funding.manage', 'budget.view', 'budget.manage']);
  const canViewExpenses = hasAnyProjectCapability(currentCapabilities, ['expense.submit', 'expense.approve', 'expense.review-accounting', 'accounting.view', 'accounting.manage']);
  const canSubmitExpense = currentCapabilities.includes('expense.submit');
  const canApproveExpense = currentCapabilities.includes('expense.approve');
  const canViewReports = hasAnyProjectCapability(currentCapabilities, ['reporting.view', 'reporting.manage', 'accounting.view', 'accounting.manage']);
  const projectMembers = selectedProject?.members || [];

  useEffect(() => {
    if (!selectedProject) return;
    if (!visibleTabs.includes(tab)) setTab('overview');
  }, [selectedProject, tab, visibleTabs]);

  // Grouped tab loaders
  useEffect(() => {
    if (!selectedProject) return;

    if (tab === 'work' || tab === 'overview') {
      setTasksLoading(true);
      getProjectTasks(selectedProject._id)
        .then((res) => setTasks(res.data.tasks || []))
        .catch((err) => console.error('Failed to load tasks:', err))
        .finally(() => setTasksLoading(false));
    }

    if (tab === 'money') {
      if (canViewBudget) {
      setBudgetLoading(true);
      getProjectBudget(selectedProject._id)
        .then((res) => {
          setBudget(res.data.budget);
          setBudgetLines(res.data.budget?.lines || []);
        })
        .catch((err) => console.error('Failed to load budget:', err))
        .finally(() => setBudgetLoading(false));
      }
      if (canViewFunding) {
      setAllocationsLoading(true);
      getProjectFundingAllocations(selectedProject._id)
        .then((res) => setAllocations(res.data.allocations || []))
        .catch((err) => console.error('Failed to load allocations:', err))
        .finally(() => setAllocationsLoading(false));
      }
      if (canViewExpenses) {
      setExpensesLoading(true);
      getProjectExpenses(selectedProject._id)
        .then((res) => setExpenses(res.data.expenses || []))
        .catch((err) => console.error('Failed to load expenses:', err))
        .finally(() => setExpensesLoading(false));
      }
    } else if (tab === 'more') {
      setMileageLoading(true);
      getProjectMileage(selectedProject._id)
        .then((res) => {
          setMileageLogs(res.data.logs || []);
          setTotalKm(res.data.totalKm || 0);
        })
        .catch((err) => console.error('Failed to load mileage:', err))
        .finally(() => setMileageLoading(false));
      setTimeLoading(true);
      getProjectTime(selectedProject._id)
        .then((res) => {
          const logs = res.data.logs || [];
          const totalMinutes = res.data.totalMinutes || 0;
          setTimeLogs(logs);
          setTotalHours(totalMinutes / 60);
        })
        .catch((err) => console.error('Failed to load time logs:', err))
        .finally(() => setTimeLoading(false));
    } else if (tab === 'reports') {
      loadSummary(selectedProject._id);
    }
  }, [selectedProject, tab, canViewBudget, canViewExpenses, canViewFunding]);

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    if (!selectedProject) return;
    setSaving(true);
    try {
      const res = await updateProject(selectedProject._id, {
        ...editData,
        client: editData.client || null,
      });
      const updated = res.data.project;
      setSelectedProject(updated);
      setProjects((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
      await loadSummary(updated._id);
      setEditing(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkSpace = async (e) => {
    e.preventDefault();
    if (!spaceIdInput.trim() || !selectedProject) return;
    setLinkingSpace(true);
    try {
      const res = await linkSpace(selectedProject._id, spaceIdInput.trim());
      setSelectedProject((prev) => ({ ...prev, spaces: res.data.project.spaces || [] }));
      setSpaceIdInput('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to link space');
    } finally {
      setLinkingSpace(false);
    }
  };

  const handleCreateWorkroom = async (e) => {
    e.preventDefault();
    if (!selectedProject || !newWorkroomName.trim() || typeof createSpace !== 'function') return;
    setCreatingWorkroom(true);
    try {
      const leadBusinessId = selectedProject.leadBusinessId?._id || selectedProject.leadBusinessId || selectedProject.businessId || null;
      const space = await createSpace({
        name: newWorkroomName.trim(),
        type: 'project',
        businessId: leadBusinessId,
        description: `Workroom for ${selectedProject.name}`,
      });
      await linkSpace(selectedProject._id, space._id);
      setNewWorkroomName('');
      setShowNewWorkroom(false);
      if (typeof fetchSpaces === 'function') await fetchSpaces();
      await refreshProject(selectedProject._id);
      navigate(`/spaces/${space._id}`);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create workroom');
    } finally {
      setCreatingWorkroom(false);
    }
  };

  const handleUnlinkSpace = async (spaceId) => {
    if (!window.confirm('Remove this space from the project?')) return;
    try {
      const res = await unlinkSpace(selectedProject._id, spaceId);
      setSelectedProject((prev) => ({ ...prev, spaces: res.data.project.spaces || [] }));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to unlink space');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedProject || !memberUserId) return;
    try {
      await addProjectMember(selectedProject._id, memberUserId, memberRole);
      setMemberUserId('');
      setMemberRole('member');
      await refreshProject(selectedProject._id);
      await loadSummary(selectedProject._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add project member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedProject) return;
    try {
      await removeProjectMember(selectedProject._id, userId);
      await refreshProject(selectedProject._id);
      await loadSummary(selectedProject._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove project member');
    }
  };

  // Budget Actions
  const handleAddBudgetLine = (e) => {
    e.preventDefault();
    if (!newLineCategory.trim()) return;
    const newLine = {
      category: newLineCategory.trim(),
      plannedAmount: Number(newLinePlanned) || 0,
      committedAmount: 0,
      actualAmount: 0,
    };
    setBudgetLines((prev) => [...prev, newLine]);
    setNewLineCategory('');
    setNewLinePlanned('');
  };

  const handleSaveBudget = async () => {
    if (!selectedProject) return;
    setSavingBudget(true);
    try {
      const res = await upsertProjectBudget(selectedProject._id, { lines: budgetLines });
      setBudget(res.data.budget);
      setBudgetLines(res.data.budget?.lines || []);
      alert('Budget saved successfully.');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to save budget');
    } finally {
      setSavingBudget(false);
    }
  };

  // Expense Actions
  const handleCreateExpense = async (e) => {
    e.preventDefault();
    if (!selectedProject || !newExpenseDesc.trim() || !newExpenseAmount) return;
    setSavingExpense(true);
    try {
      const splits = newExpenseCategory
        ? [{ budgetLineCategory: newExpenseCategory, fundingSourceId: newExpenseSourceId || null, amount: Number(newExpenseAmount) }]
        : [];
      const res = await createProjectExpense(selectedProject._id, {
        description: newExpenseDesc.trim(),
        amount: Number(newExpenseAmount),
        currency: 'CAD',
        splits,
      });
      setExpenses((prev) => [res.data.expense, ...prev]);
      setShowNewExpense(false);
      setNewExpenseDesc('');
      setNewExpenseAmount('');
      setNewExpenseCategory('');
      setNewExpenseSourceId('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create expense');
    } finally {
      setSavingExpense(false);
    }
  };

  const handleTransitionExpense = async (expenseId, targetState) => {
    try {
      const res = await transitionExpenseState(expenseId, { state: targetState });
      setExpenses((prev) => prev.map((exp) => (exp._id === expenseId ? res.data.expense : exp)));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to transition expense state');
    }
  };

  const openProject = (p) => {
    navigate(`/projects/${p._id}`);
  };

  const handleCreateGlobalProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim() || !newProjectBusinessId) return;
    setCreatingProject(true);
    try {
      const res = await createProject({
        name: newProjectName.trim(),
        description: newProjectDescription.trim(),
        leadBusinessId: newProjectBusinessId,
        lifecycleState: 'idea',
      });
      const created = res.data.project;
      setProjects((prev) => [created, ...prev]);
      setNewProjectName('');
      setNewProjectDescription('');
      setShowCreateProject(false);
      navigate(`/projects/${created._id}`);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  };

  if (!isDetailView) {
    return (
      <div className="project-list-page">
        <div className="project-list-header">
          <div>
            <h1 className="project-list-title">Projects</h1>
            <p className="dashboard-sub">Your authorized projects across Business contexts</p>
          </div>
          {businesses.length > 0 && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreateProject((v) => !v)}>
              + New Project
            </button>
          )}
        </div>
        {showCreateProject && (
          <form className="project-create-panel" onSubmit={handleCreateGlobalProject}>
            <select
              className="form-select"
              value={newProjectBusinessId}
              onChange={(e) => setNewProjectBusinessId(e.target.value)}
              required
            >
              <option value="">Choose lead Business *</option>
              {businesses.map((business) => (
                <option key={business._id} value={business._id}>{business.name}</option>
              ))}
            </select>
            <input
              className="form-input"
              placeholder="Project name *"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              required
            />
            <input
              className="form-input"
              placeholder="Short description"
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e.target.value)}
            />
            <button className="btn btn-primary btn-sm" type="submit" disabled={creatingProject}>
              {creatingProject ? 'Creating...' : 'Create Project'}
            </button>
          </form>
        )}
        {listLoading ? (
          <p className="panel-muted">Loading projects...</p>
        ) : projects.length === 0 ? (
          <p className="panel-muted">No projects found.</p>
        ) : (
          <ul className="project-list">
            {projects.map((p) => (
              <li key={p._id} className="project-list-item" onClick={() => openProject(p)}>
                <div className="project-list-info">
                  <div className="project-list-title-row">
                    <span className="project-list-name">{p.name}</span>
                    <span className={`project-status-badge status-${p.lifecycleState || p.status}`}>{formatLifecycle(p.lifecycleState || p.status)}</span>
                  </div>
                  {p.leadBusinessId && (
                    <span className="project-list-client">
                      Lead Business: {p.leadBusinessId?.name || 'Assigned'}
                    </span>
                  )}
                  {p.description && <span className="project-list-desc">{p.description}</span>}
                </div>
                <span className="project-item-arrow">›</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (!selectedProject) {
    return <div className="project-detail-page"><p className="panel-muted">Loading...</p></div>;
  }

  const primarySpaceId = selectedProject.primarySpaceId?._id || selectedProject.primarySpaceId;
  const primarySpace = selectedProject.primarySpaceId?._id
    ? selectedProject.primarySpaceId
    : spaces.find((s) => String(s._id) === String(primarySpaceId));
  const upcomingTasks = tasks.slice(0, 4);

  return (
    <div className="project-detail-page">
      <div className="project-detail-header">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/projects')}>← Projects</button>
        <div className="project-detail-title-area">
          <h1 className="project-detail-name">{selectedProject.name}</h1>
          <div className="project-header-meta-row">
            {selectedProject.leadBusinessId && (
              <span className="project-lead-badge">
                Lead Business: {selectedProject.leadBusinessId?.name || selectedProject.leadBusinessId}
              </span>
            )}
            {primarySpace && (
              <Link to={`/spaces/${primarySpace._id}`} className="project-space-pill">
                💬 Primary Space: {primarySpace.name} →
              </Link>
            )}
          </div>
        </div>
        <span className={`project-status-badge status-${selectedProject.lifecycleState || selectedProject.status}`}>
          {formatLifecycle(selectedProject.lifecycleState || selectedProject.status)}
        </span>
      </div>

      <div className="project-tabs">
        {visibleTabs.map((t) => (
          <button
            key={t}
            className={`project-tab-btn ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {tabLabel(t)}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div className="project-overview">
          {editing ? (
            <form onSubmit={handleUpdateProject} className="project-edit-form">
              <div className="form-group">
                <label className="form-label">Project Name *</label>
                <input
                  className="form-input"
                  value={editData.name || ''}
                  onChange={(e) => setEditData((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Lifecycle State</label>
                <select
                  className="form-select"
                  value={editData.lifecycleState || 'active'}
                  onChange={(e) => setEditData((p) => ({ ...p, lifecycleState: e.target.value }))}
                >
                  {LIFECYCLE_STATES.map((st) => <option key={st} value={st}>{formatLifecycle(st)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={editData.description || ''}
                  onChange={(e) => setEditData((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div className="project-info-card">
              <div className="project-card-header">
                <h3>Overview</h3>
                {canManageProject && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Edit</button>}
              </div>
              <p className="project-desc-text">{selectedProject.description || 'No description provided.'}</p>

              <div className="project-details-grid">
                <div className="detail-item">
                  <span className="detail-label">Lifecycle State:</span>
                  <span className="detail-val">{formatLifecycle(selectedProject.lifecycleState || selectedProject.status)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Lead Business:</span>
                  <span className="detail-val">{selectedProject.leadBusinessId?.name || 'Owner'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Primary Space:</span>
                  <span className="detail-val">
                    {primarySpace ? (
                      <Link to={`/spaces/${primarySpace._id}`}>{primarySpace.name} (Open)</Link>
                    ) : (
                      'None'
                    )}
                  </span>
                </div>
              </div>

              <div className="project-overview-grid">
                <div className="project-overview-panel">
                  <span>Responsible</span>
                  <strong>{selectedProject.projectManagerIds?.[0]?.name || projectMembers[0]?.user?.name || 'Not assigned'}</strong>
                </div>
                <div className="project-overview-panel">
                  <span>Team</span>
                  <strong>{projectMembers.length || 0} people</strong>
                </div>
                <div className="project-overview-panel">
                  <span>Next Tasks</span>
                  <strong>{tasksLoading ? 'Loading...' : upcomingTasks.length}</strong>
                </div>
                <div className="project-overview-panel">
                  <span>Reports</span>
                  <strong>{canViewReports ? 'Available' : 'Not shown'}</strong>
                </div>
              </div>

              {upcomingTasks.length > 0 && (
                <div className="project-sub-section">
                  <div className="project-card-header compact">
                    <h4>What is happening next</h4>
                    <button className="btn btn-ghost btn-sm" onClick={() => setTab('work')}>Open Work</button>
                  </div>
                  <ul className="project-task-list compact">
                    {upcomingTasks.map((task) => (
                      <li key={task._id} className="project-task-item">
                        <div className="task-left"><strong>{task.title}</strong><p>{task.description || 'No description'}</p></div>
                        <span className={`task-pill status-${task.status}`}>{task.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── WORK TAB ── */}
      {tab === 'work' && (
        <div className="project-work-view">
          <div className="team-header-card">
            <div>
              <h3>Work</h3>
              <p className="dashboard-sub">Conversation, tasks and collaboration for this Project</p>
            </div>
            <div className="project-action-row">
              {primarySpace && <Link to={`/spaces/${primarySpace._id}`} className="btn btn-primary btn-sm">Open Primary Space</Link>}
              {canManageProject && <button className="btn btn-ghost btn-sm" onClick={() => setShowNewWorkroom((value) => !value)}>+ New workroom</button>}
              {canSubmitExpense && <button className="btn btn-ghost btn-sm" onClick={() => { setTab('money'); setShowNewExpense(true); }}>Submit Expense</button>}
            </div>
          </div>

          {showNewWorkroom && canManageProject && (
            <form className="project-new-workroom" onSubmit={handleCreateWorkroom}>
              <input
                className="form-input"
                placeholder="Workroom name *"
                value={newWorkroomName}
                onChange={(e) => setNewWorkroomName(e.target.value)}
                required
              />
              <span className="project-new-workroom-context">
                Linked to {selectedProject.name}{selectedProject.leadBusinessId?.name ? ` / ${selectedProject.leadBusinessId.name}` : ''}
              </span>
              <button className="btn btn-primary btn-sm" type="submit" disabled={creatingWorkroom}>
                {creatingWorkroom ? 'Creating...' : 'Create workroom'}
              </button>
            </form>
          )}

          <div className="project-info-card">
            <div className="project-card-header">
              <h3>Tasks ({tasks.length})</h3>
            </div>
            {tasksLoading ? (
              <p className="panel-muted">Loading tasks...</p>
            ) : tasks.length === 0 ? (
              <p className="panel-muted">No tasks assigned to this project.</p>
            ) : (
              <ul className="project-task-list">
                {tasks.map((task) => (
                  <li key={task._id} className="project-task-item">
                    <div className="task-left">
                      <strong>{task.title}</strong>
                      <p>{task.description || 'No description'}</p>
                    </div>
                    <span className={`task-pill status-${task.status}`}>{task.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="project-tool-links">
            <Link to="/calendar">Calendar</Link>
            <Link to="/files">Files</Link>
            <Link to="/time">Time</Link>
            <Link to="/mileage">Mileage</Link>
          </div>
        </div>
      )}

      {/* ── MONEY TAB ── */}
      {tab === 'money' && canViewBudget && (
        <div className="project-budget-view">
          <div className="budget-header-card">
            <h3>Project Budget (v{budget?.version || 1})</h3>
            <div className="budget-metrics-grid">
              <div className="metric-box">
                <span className="metric-label">Total Planned</span>
                <strong className="metric-val">${(budget?.totalPlanned || 0).toLocaleString()}</strong>
              </div>
              <div className="metric-box">
                <span className="metric-label">Committed</span>
                <strong className="metric-val">${(budget?.totalCommitted || 0).toLocaleString()}</strong>
              </div>
              <div className="metric-box">
                <span className="metric-label">Actual Incurred</span>
                <strong className="metric-val">${(budget?.totalActual || 0).toLocaleString()}</strong>
              </div>
              <div className="metric-box highlight">
                <span className="metric-label">Unspent Balance</span>
                <strong className="metric-val">
                  ${Math.max(0, (budget?.totalPlanned || 0) - (budget?.totalActual || 0)).toLocaleString()}
                </strong>
              </div>
            </div>
          </div>

          <div className="budget-lines-card">
            <h4>Budget Lines</h4>
            {budgetLoading ? (
              <p className="panel-muted">Loading budget...</p>
            ) : budgetLines.length === 0 ? (
              <p className="panel-muted">No budget lines defined yet.</p>
            ) : (
              <table className="budget-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Planned</th>
                    <th>Committed</th>
                    <th>Actual</th>
                    <th>Available</th>
                  </tr>
                </thead>
                <tbody>
                  {budgetLines.map((line, idx) => (
                    <tr key={idx}>
                      <td><strong>{line.category}</strong></td>
                      <td>${(line.plannedAmount || 0).toLocaleString()}</td>
                      <td>${(line.committedAmount || 0).toLocaleString()}</td>
                      <td>${(line.actualAmount || 0).toLocaleString()}</td>
                      <td>${Math.max(0, (line.plannedAmount || 0) - (line.actualAmount || 0)).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Add Line Form */}
            {canManageBudget && <form onSubmit={handleAddBudgetLine} className="add-budget-line-form">
              <input
                className="form-input"
                placeholder="Category (e.g. Field Operations) *"
                value={newLineCategory}
                onChange={(e) => setNewLineCategory(e.target.value)}
                required
              />
              <input
                type="number"
                className="form-input form-input-sm"
                placeholder="Planned Amount ($) *"
                value={newLinePlanned}
                onChange={(e) => setNewLinePlanned(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-ghost btn-sm">+ Add Line</button>
              {budgetLines.length > 0 && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveBudget}
                  disabled={savingBudget}
                >
                  {savingBudget ? 'Saving...' : 'Save Budget Version'}
                </button>
              )}
            </form>}
          </div>
        </div>
      )}

      {tab === 'money' && canViewFunding && (
        <div className="project-funding-view">
          <div className="funding-header-card">
            <h3>Funding Allocations</h3>
            <p className="dashboard-sub">Grant agreements and funding sources supporting this project</p>
            <div className="funding-waterfall-note">
              <strong>Advisory Allocation Priority Waterfall:</strong> Grants / Government &gt; Earmarked Donations &gt; General Donations &gt; Econ-Dev Contributions &gt; Internal (RRS)
            </div>
          </div>

          {allocationsLoading ? (
            <p className="panel-muted">Loading funding allocations...</p>
          ) : allocations.length === 0 ? (
            <p className="panel-muted">No funding allocations assigned to this project yet.</p>
          ) : (
            <div className="allocations-grid">
              {allocations.map((alloc) => (
                <div key={alloc._id} className="allocation-card">
                  <div className="alloc-header">
                    <h4>{alloc.fundingSourceId?.name || 'Funding Source'}</h4>
                    <span className="alloc-funder">{alloc.fundingSourceId?.fundingBody || 'Grant'}</span>
                  </div>
                  <div className="alloc-body">
                    <div className="alloc-metric">
                      <span>Allocated:</span>
                      <strong>${(alloc.allocatedAmount || 0).toLocaleString()}</strong>
                    </div>
                    <div className="alloc-metric">
                      <span>Committed:</span>
                      <strong>${(alloc.committedAmount || 0).toLocaleString()}</strong>
                    </div>
                    <div className="alloc-metric">
                      <span>Spent:</span>
                      <strong>${(alloc.actualAmount || 0).toLocaleString()}</strong>
                    </div>
                    <div className="alloc-metric highlight">
                      <span>Available to Commit:</span>
                      <strong>${(alloc.availableToCommitAmount ?? Math.max(0, alloc.allocatedAmount - alloc.actualAmount - alloc.committedAmount)).toLocaleString()}</strong>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'money' && canViewExpenses && (
        <div className="project-expenses-view">
          <div className="expenses-header-card">
            <div>
              <h3>Project Expenses &amp; Receipts</h3>
              <p className="dashboard-sub">Receipts, approvals and spending for this Project</p>
            </div>
            {canSubmitExpense && <button className="btn btn-primary" onClick={() => setShowNewExpense((v) => !v)}>
              {showNewExpense ? '✕ Cancel' : '+ Submit Expense'}
            </button>}
          </div>

          {showNewExpense && canSubmitExpense && (
            <form onSubmit={handleCreateExpense} className="new-expense-form-card">
              <h4>Submit New Project Expense</h4>
              <div className="form-row">
                <input
                  className="form-input"
                  placeholder="Expense description *"
                  value={newExpenseDesc}
                  onChange={(e) => setNewExpenseDesc(e.target.value)}
                  required
                />
                <input
                  type="number"
                  className="form-input"
                  placeholder="Amount ($) *"
                  value={newExpenseAmount}
                  onChange={(e) => setNewExpenseAmount(e.target.value)}
                  required
                />
              </div>
              <div className="form-row">
                <input
                  className="form-input"
                  placeholder="Budget Line Category (optional)"
                  value={newExpenseCategory}
                  onChange={(e) => setNewExpenseCategory(e.target.value)}
                />
                <button type="submit" className="btn btn-primary" disabled={savingExpense}>
                  {savingExpense ? 'Submitting...' : 'Submit for PM Review'}
                </button>
              </div>
            </form>
          )}

          {expensesLoading ? (
            <p className="panel-muted">Loading expenses...</p>
          ) : expenses.length === 0 ? (
            <p className="panel-muted">No expenses recorded for this project yet.</p>
          ) : (
            <table className="expenses-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>State</th>
                  <th>Incurred By</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => (
                  <tr key={exp._id}>
                    <td><strong>{exp.description}</strong></td>
                    <td>${(exp.amount || 0).toLocaleString()} {exp.currency}</td>
                    <td><span className={`expense-state-tag state-${exp.state}`}>{exp.state}</span></td>
                    <td>{exp.incurredBy?.name || 'Member'}</td>
                    <td>{new Date(exp.transactionDate || exp.createdAt).toLocaleDateString()}</td>
                    <td className="expense-actions-cell">
                      {exp.state === 'draft' && (
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => handleTransitionExpense(exp._id, 'submitted')}
                        >
                          Submit
                        </button>
                      )}
                      {exp.state === 'submitted' && canApproveExpense && (
                        <button
                          className="btn btn-xs btn-primary"
                          onClick={() => handleTransitionExpense(exp._id, 'pm-approved')}
                        >
                          PM Approve
                        </button>
                      )}
                      {exp.state === 'pm-approved' && (
                        <button
                          className="btn btn-xs btn-ghost"
                          onClick={() => handleTransitionExpense(exp._id, 'accounting-review')}
                        >
                          Send to Accounting
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── MORE TAB ── */}
      {tab === 'more' && (
        <div className="project-team-view">
          <div className="team-header-card">
            <div>
              <h3>More</h3>
              <p className="dashboard-sub">People, Spaces, settings, Time and Mileage for this Project</p>
            </div>
          </div>

          {canManageProject && (
          <form onSubmit={handleAddMember} className="add-member-form">
            <select
              className="form-select"
              value={memberUserId}
              onChange={(e) => setMemberUserId(e.target.value)}
              required
            >
              <option value="">Select user to add...</option>
              {allUsers.map((u) => (
                <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
              ))}
            </select>
            <select
              className="form-select"
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value)}
            >
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button type="submit" className="btn btn-primary btn-sm">Add Member</button>
          </form>
          )}

          <div className="members-list-card">
            <h4>People ({selectedProject.members?.length || 0})</h4>
            {selectedProject.members?.length === 0 ? (
              <p className="panel-muted">No explicit members assigned.</p>
            ) : (
              <ul className="project-members-list">
                {selectedProject.members.map((m) => {
                  const uid = m.user?._id || m.user;
                  const uObj = allUsers.find((u) => String(u._id) === String(uid)) || m.user;
                  return (
                    <li key={uid} className="project-member-item">
                      <div className="member-info">
                        <strong>{uObj?.name || 'User'}</strong>
                        <span className="member-role-tag">{m.role}</span>
                      </div>
                      {canManageProject && (
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => handleRemoveMember(uid)}
                        title="Remove member"
                      >
                        ✕
                      </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="project-info-card">
            <div className="project-card-header">
              <h3>Additional Spaces ({selectedProject.spaces?.length || 0})</h3>
            </div>
            {canManageProject && (
              <form onSubmit={handleLinkSpace} className="link-space-form">
                <select className="form-select" value={spaceIdInput} onChange={(e) => setSpaceIdInput(e.target.value)}>
                  <option value="">Select space to link...</option>
                  {spaces.map((space) => <option key={space._id} value={space._id}>{space.name}</option>)}
                </select>
                <button type="submit" className="btn btn-primary btn-sm" disabled={linkingSpace || !spaceIdInput}>
                  {linkingSpace ? 'Linking...' : 'Link Space'}
                </button>
              </form>
            )}
            {selectedProject.spaces?.length > 0 ? (
              <ul className="project-spaces-list">
                {selectedProject.spaces.map((space) => {
                  const spaceId = space._id || space;
                  const spaceObj = space._id ? space : spaces.find((entry) => String(entry._id) === String(spaceId));
                  return (
                    <li key={spaceId} className="project-space-item">
                      <Link to={`/spaces/${spaceId}`}>{spaceObj?.name || 'Space'}</Link>
                      {canManageProject && <button className="btn-icon btn-danger" onClick={() => handleUnlinkSpace(spaceId)} title="Unlink">✕</button>}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="panel-muted">No additional Spaces linked yet.</p>
            )}
          </div>

          <div className="project-info-card">
            <div className="project-card-header">
              <h3>Project Settings</h3>
              {canManageProject && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Edit Settings</button>}
            </div>
            <div className="project-details-grid">
              <div className="detail-item"><span className="detail-label">Visibility</span><span className="detail-val">{selectedProject.visibility}</span></div>
              <div className="detail-item"><span className="detail-label">Start</span><span className="detail-val">{selectedProject.startDate ? new Date(selectedProject.startDate).toLocaleDateString() : 'Not set'}</span></div>
              <div className="detail-item"><span className="detail-label">End</span><span className="detail-val">{selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString() : 'Not set'}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ── MILEAGE TAB ── */}
      {tab === 'more' && (
        <div className="project-mileage-view">
          <div className="mileage-header">
            <h3>Mileage</h3>
            <strong>Total: {totalKm} km</strong>
          </div>
          {mileageLoading ? (
            <p className="panel-muted">Loading trips...</p>
          ) : mileageLogs.length === 0 ? (
            <p className="panel-muted">No trips logged for this project.</p>
          ) : (
            <ul className="project-mileage-list">
              {mileageLogs.slice(0, 6).map((log) => (
                <li key={log._id} className="project-log-item">
                  <span>{log.description || 'Trip'}{log.date && ` - ${new Date(log.date).toLocaleDateString()}`}</span>
                  <strong>{log.kilometers} km</strong>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── TIME TAB ── */}
      {tab === 'more' && (
        <div className="project-time-view">
          <div className="time-header">
            <h3>Time</h3>
            <strong>Total: {totalHours.toFixed(1)} hrs</strong>
          </div>
          {timeLoading ? (
            <p className="panel-muted">Loading time entries...</p>
          ) : timeLogs.length === 0 ? (
            <p className="panel-muted">No time logged for this project.</p>
          ) : (
            <ul className="project-time-list">
              {timeLogs.slice(0, 6).map((log) => (
                <li key={log._id} className="project-log-item">
                  <span>{log.description || 'Work entry'}{log.date && ` - ${new Date(log.date).toLocaleDateString()}`}</span>
                  <strong>{((log.minutes || 0) / 60).toFixed(1)} hrs</strong>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── REPORTS TAB ── */}
      {tab === 'reports' && (
        <div className="project-reports-view">
          <h3>Project Summary Report</h3>
          {summaryLoading ? (
            <p className="panel-muted">Loading summary report...</p>
          ) : summaryError ? (
            <p className="form-error">{summaryError}</p>
          ) : summary ? (
            <div className="summary-metrics-grid">
              <div className="metric-card">
                <span>Total Mileage</span>
                <strong>{summary.totalKilometers || 0} km</strong>
              </div>
              <div className="metric-card">
                <span>Total Time</span>
                <strong>{summary.totalHours || 0} hrs</strong>
              </div>
              <div className="metric-card">
                <span>Tasks Count</span>
                <strong>{summary.tasksCount || 0}</strong>
              </div>
            </div>
          ) : (
            <p className="panel-muted">No report data available.</p>
          )}
        </div>
      )}
    </div>
  );
}
